import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  CarouselProvider, Slider, Slide, ButtonBack, ButtonNext,
} from 'pure-react-carousel';
import {
  Icon, Link, Button,
} from '@resy/ui-kitchen';
import { sanitizeString } from '../../../../../../../ui-kitchen/src/utils/sanitizeString';
import Chevron from '../../../../../images/icons/chevron-left.svg';
import WrapperExperientialEventsCard from '../../WrapperExperientialEventsCard';
import AmexCenturionNYEventCard from '../../../AmexCenturionNY/AmexCenturionNYEventCard';
import { handleMktAnalytics } from '../../AmexUtils';
import { EVENT_CONTEXT, CTA_EVENT_NAME } from '../../constants';
import { EVENT_TYPES, EVENTS_LENGTH_2 } from '../../../AmexCenturionNY/constants';
import './LayoutCarousel.scss';

const LayoutCarousel = (props) => {
  const {
    content: {
      sr_info: srInfo = '',
      sr_controls_aria: srControlsAria = '',
    },
    content,
    eventsContent,
    venueContent,
    eventContext,
    matchMedia,
    Analytics,
    tabHeading: heading,
  } = props;

  let displayArray = '';
  if (eventContext === EVENT_CONTEXT.PAST_EVENTS) {
    if (!eventsContent) return null;
    displayArray = eventsContent;
  } else if (eventContext === EVENT_CONTEXT.EXPERIENTIAL_EVENTS) {
    if (!eventsContent?.experientialEvents) return null;
    displayArray = eventsContent?.experientialEvents;
  }

  const [seeAllPastEventsClicked, setSeeAllPastEventsClicked] = useState(false);
  const eventLength = displayArray?.length;

  const sanitizedHeading = sanitizeString(heading);

  const renderButtons = () => (
    <>
      <ButtonBack
        className="LayoutCarousel__carousel__back-button"
      >
        <span aria-hidden="true">
          <Icon
            name="Chevron"
            component={Chevron}
          />
        </span>
      </ButtonBack>
      <ButtonNext
        className="LayoutCarousel__carousel__next-button"
      >
        <span aria-hidden="true">
          <Icon
            name="Chevron"
            component={Chevron}
          />
        </span>
      </ButtonNext>
    </>
  );

  const renderCardForCarousel = (item) => {
    const cardProps = {
      ...item,
      event_type_id: EVENT_TYPES.API,
      eventButtons: content?.event_card_cta,
      isDesktop: matchMedia.md,
      Analytics,
      venue: {
        location: venueContent?.location,
        urlSlug: venueContent?.urlSlug,
      },
      eventContext,
      tabHeading: heading,
      hideMoreInfoCta: content?.hide_more_info_cta,
    };
    switch (eventContext) {
      case EVENT_CONTEXT.PAST_EVENTS:
        return matchMedia.md ? (
          <AmexCenturionNYEventCard
            {...cardProps}
          />
        ) : (
          <li key={item.name}>
            <AmexCenturionNYEventCard
              {...cardProps}
            />
          </li>
        );
      case EVENT_CONTEXT.EXPERIENTIAL_EVENTS:
      {
        return (
          <WrapperExperientialEventsCard
            {...props}
            taggingConfig={content?.action_names}
            eventItem={item}
            isCarousel
          />
        ); }
      default:
        return null;
    }
  };
  const renderDesktop = () => (
    <div
      className="LayoutCarousel__carousel"
      role="region"
    >
      <p className="sr-only">
        {srInfo}
      </p>
      <CarouselProvider
        visibleSlides={2}
        totalSlides={eventLength}
        step={2}
        dragEnabled={false}
        naturalSlideWidth={400}
        naturalSlideHeight={650}
        isIntrinsicHeight="true"
        id="eventsCarousel"
        aria-roledescription="carousel"
      >
        <div className="LayoutCarousel__carousel__container">
          <div
            role="group"
            aria-label={srControlsAria}
            className="LayoutCarousel__carousel__controls"
          >
            {eventLength > 2 && renderButtons()}
          </div>
          <Slider>
            {displayArray && displayArray.map((item, index) => (
              <Slide
                tabIndex={-1}
                index={index}
                key={item.event_name}
                aria-label={`${index + 1} of ${eventLength}`}
                aria-roledescription="slide"
              >
                {renderCardForCarousel(item)}
              </Slide>
            ))}
          </Slider>
        </div>
      </CarouselProvider>
    </div>
  );

  const renderCardsForMobile = () => {
    const cards = [];
    let calculatedEventLen = 0;
    if (seeAllPastEventsClicked) {
      calculatedEventLen = eventLength;
    } else {
      calculatedEventLen = eventLength < EVENTS_LENGTH_2 ? eventLength : EVENTS_LENGTH_2;
    }
    for (let i = 0; i < calculatedEventLen; i += 1) {
      const item = displayArray[i];
      cards.push((
        renderCardForCarousel(item)
      ));
    }
    return cards;
  };

  const renderMobile = () => (
    <div className="LayoutCarousel__wrapper">
      <ul className="LayoutCarousel__grid">
        {renderCardsForMobile()}
      </ul>
    </div>
  );

  return (
    <div className="LayoutCarousel">
      {heading && (
        <h3
          className="LayoutCarousel__heading content-heading"
          dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
            __html: sanitizedHeading,
          }}
        />
      )}
      <div className="LayoutCarousel__inner">
        {!matchMedia.md ? renderMobile() : renderDesktop()}
        {eventContext === EVENT_CONTEXT.EXPERIENTIAL_EVENTS
            && content?.cta?.label && (
            <div className="LayoutCarousel__bottom">
              <Link
                target="_blank"
                rel="noopener noreferrer"
                href={content?.cta?.url}
                className="LayoutCarousel__ctaLink"
                aria-label={`${heading} ${content?.cta?.label}`}
                onClick={() => handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, {
                  action_name: `${heading} - ${content?.cta?.label} Click`,
                  tab_item: heading,
                })}
              >
                {content?.cta?.label}
              </Link>
            </div>
        )}
        {(!matchMedia.md
              && (eventContext === EVENT_CONTEXT.PAST_EVENTS)
              && content?.cta?.label
              && (eventLength > EVENTS_LENGTH_2)
              && seeAllPastEventsClicked === false) && (
                <div className="LayoutCarousel__bottom">
                  <Button
                    target="_blank"
                    rel="noopener noreferrer"
                    className="LayoutCarousel__ctaButton"
                    onClick={() => setSeeAllPastEventsClicked(true)}
                  >
                    {content?.cta?.label}
                  </Button>
                </div>
        )}
      </div>
    </div>
  );
};

LayoutCarousel.propTypes = {
  content: PropTypes.shape({
    cta: PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      action_name: PropTypes.string.isRequired,
    }),
    article_name: PropTypes.string,
    event_card_cta: PropTypes.string,
    hide_more_info_cta: PropTypes.bool,
    event_tags: PropTypes.shape({}).isRequired,
    action_names: PropTypes.shape({}).isRequired,
    sr_info: PropTypes.string.isRequired,
    sr_controls_aria: PropTypes.string.isRequired,
  }).isRequired,
  eventsContent: PropTypes.shape({
    experientialEvents: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  tabHeading: PropTypes.string,
  venueContent: PropTypes.shape({
    location: PropTypes.shape({}),
    urlSlug: PropTypes.string.isRequired,
  }),
  eventContext: PropTypes.string.isRequired,
};

LayoutCarousel.defaultProps = {
  tabHeading: '',
  venueContent: {},
};

export default LayoutCarousel;
